import React, { useState } from 'react';
import styled from 'styled-components';
import { Body5, Body6, Container, H3, Page } from '@ui';
import { Body } from '@ui';
import { Header } from '@app/navigation/header';
import { useNavigate } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { useLatestDocs } from '@hooks/features/useLatestDocs';
import { LatestDocsList } from '@containers/DigitalAnalyst/DocumentList';
import { logNetRequest, scrollToTop } from '@utils/usefulFuncs';
import { PageLoading } from '@ui/PageLoading';
import { ROUTES } from '@constants/routes';
import { Loading } from '@ui/Loading';
import { createRows } from '@utils';
import { PageError } from '@ui/PageError';
import BackArrowButton from '@ui/buttons/BackArrowButton';
import { FlatButton } from '@ui/buttons';
import { sendApiRequest } from '@services/api';

const PageWrapper = styled.div`
  height: 100vh;
`;
const BodyWrapper = styled.div`
  padding: 24px;
`;

async function fetchSecurityData(id) {
  const URL = `v1/genai_companies?company_id=${id}`;
  const { status, data, error } = await sendApiRequest('get', URL);
  logNetRequest(status, data);
  if (status === 200) {
    return {
      ok: true,
      data: data[0],
    };
  }
  return { status, error };
}

function StockDetailDisplay({ security }) {
  const navigate = useNavigate();

  const [activeSecurity, setActiveSecurity] = useState(null);
  const [rows, setRows] = React.useState([]);
  const {
    latestDocDays,
    latestDocsByDayLookup,

    isLoading: isLoadingRecentDocs,
    getTickerDocData,
  } = useLatestDocs();

  React.useEffect(() => {
    if (security && !activeSecurity) {
      setActiveSecurity(security);
    }
    if (security && activeSecurity && security?.symbol !== activeSecurity?.symbol) {
      setActiveSecurity(security);
    }
  }, [security, activeSecurity]);

  React.useEffect(() => {
    if (activeSecurity) {
      const symbol = activeSecurity.symbol;
      getTickerDocData({ symbols: [symbol] });
    }
  }, [activeSecurity]);

  React.useEffect(() => {
    if (Array.isArray(latestDocDays)) {
      setRows(createRows(latestDocDays, latestDocsByDayLookup, []));
    }
  }, [latestDocDays, latestDocsByDayLookup]);

  const onGoBack = () => {
    navigate(ROUTES.home);
  };

  const onQueryNowClick = () => {
    const url = ROUTES.digitalAnalyst + '?ticker=' + activeSecurity.symbol;
    window.open(url, '_blank');
  };

  if (!security) {
    return (
      <PageWrapper>
        <Header />
        <Body withFooter>
          <BodyWrapper>
            <Page>
              <BackArrowButton onClick={onGoBack} />
              <Container top={36} bottom={16}>
                <Body6 bold>Something went wrong, please try again later...</Body6>
              </Container>
            </Page>
          </BodyWrapper>
        </Body>
      </PageWrapper>
    );
  }

  return (
    <PageWrapper>
      <Header />
      <Body withFooter>
        <BodyWrapper>
          <Page>
            <BackArrowButton onClick={onGoBack} />
            <Container row verticallyCenter spaceBetween top={48}>
              <Container>
                {security?.symbol && (
                  <H3 bold lineHeight={'28px'}>
                    {security?.symbol}
                  </H3>
                )}
                <Container top={4}>
                  <Body5>{security?.name}</Body5>
                </Container>
              </Container>
              <Container left={16}>
                <FlatButton onClick={onQueryNowClick}>Query Now</FlatButton>
              </Container>
            </Container>

            <Container>
              <Container vertical={24}>
                {isLoadingRecentDocs || activeSecurity?.symbol !== security?.symbol ? (
                  <Loading />
                ) : (
                  <LatestDocsList rows={rows} enableSummaryMode />
                )}
              </Container>
            </Container>
          </Page>
        </BodyWrapper>
      </Body>
    </PageWrapper>
  );
}

function StockDetailGetter({ id }) {
  const [security, setSecurity] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  React.useEffect(() => {
    const getter = async () => {
      const { ok, data } = await fetchSecurityData(id);
      if (ok) {
        setSecurity(data);
        setIsLoading(false);
        setError(null);
      } else {
        setError('Something went wrong, please refresh the page and try again.');
        setIsLoading(false);
      }
    };
    getter();
  }, [id]);

  if (isLoading) {
    return <PageLoading />;
  }

  if (error) {
    return <PageError message={error} />;
  }

  if (!security) {
    return <PageError message={'This stock is currently unavailable. Please try again later...'} />;
  }

  return <StockDetailDisplay security={security} />;
}

export default function StockDetail() {
  const navigate = useNavigate();

  const [searchParams] = useSearchParams();
  const [companyId, setCompanyId] = React.useState(null);
  const [docTagId, setDocTagId] = React.useState(null);

  React.useEffect(() => {
    const companyId = searchParams.get('id');
    if (companyId) {
      setCompanyId(companyId);
      setDocTagId(null);
    }
    if (!companyId && !docTagId) {
      navigate(ROUTES.home);
    }
  }, [searchParams]);

  React.useEffect(() => {
    scrollToTop();
  }, []);

  if (companyId !== null) {
    return <StockDetailGetter id={companyId} />;
  }
  return null;
}
